/**
 * GiftCardThankYouPage.jsx
 *
 * Buyer redirected here after a successful Stripe Checkout for a gift card.
 * Fetches a public lookup endpoint to surface delivery status + operator
 * contact info ("any issues, reach out to {Org}").
 *
 * Stripe's own buyer receipt is sent automatically by Stripe; our own
 * branded receipt is sent by the webhook (see gift_card_delivery.py).
 */
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Mail, Gift, Home, Phone, Globe, Loader2 } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtMoney = (amount, currency = "USD") => {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: (currency || "USD").toUpperCase(),
      currencyDisplay: "narrowSymbol",
    }).format(Number(amount || 0));
  } catch {
    return `${Number(amount || 0).toFixed(2)} ${currency}`;
  }
};

const fmtScheduled = (iso, tz) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric", month: "long", day: "numeric",
      timeZone: tz || undefined,
    });
  } catch {
    return iso.slice(0, 10);
  }
};

export default function GiftCardThankYouPage() {
  const [params] = useSearchParams();
  const purchaseId = params.get("purchase_id");
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(!!purchaseId);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  useEffect(() => {
    if (!purchaseId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(
          `${API}/v2/public/gift-card-purchases/lookup/${purchaseId}`,
        );
        if (!cancelled) setInfo(data);
      } catch {
        // Silently degrade — page still renders a generic confirmation
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [purchaseId]);

  const accent = info?.org_primary_color || "#0f172a";
  const scheduledLine = info?.scheduled_delivery_at
    ? `We'll email it to ${info.recipient_name || "your recipient"} on ${fmtScheduled(info.scheduled_delivery_at)}.`
    : `We've emailed the gift card to ${info?.recipient_name ? `${info.recipient_name} (${info.recipient_email_masked || ""})` : (info?.recipient_email_masked || "your recipient")}.`;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex flex-col" data-testid="gift-card-thankyou-page">
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="max-w-md w-full bg-white rounded-3xl shadow-md border border-gray-100 p-8 sm:p-10">
          {/* Hero */}
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-100 mb-5">
              <CheckCircle2 className="w-9 h-9 text-emerald-600" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Your gift card is on its way</h1>
            {info && (
              <p className="text-sm text-gray-500 mb-3">
                {fmtMoney(info.amount, info.currency)} for {info.org_name}
              </p>
            )}
            <p className="text-gray-600 mb-8 leading-relaxed">
              {loading ? "Confirming your order…" : info ? scheduledLine : "Your recipient will receive a beautiful email with a link to view their gift card."}
            </p>
          </div>

          {/* What's next */}
          <div className="bg-gray-50 rounded-2xl p-5 space-y-3 text-left mb-6">
            <div className="flex items-start gap-3 text-sm text-gray-700">
              <Mail className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
              <span>A receipt is on its way to your inbox — keep an eye on spam just in case.</span>
            </div>
            <div className="flex items-start gap-3 text-sm text-gray-700">
              <Gift className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
              <span>The recipient gets a secure link to view their card. The code is masked until they're ready to use it.</span>
            </div>
          </div>

          {/* Operator contact — the "any issues, reach out" panel */}
          {info && (info.org_contact_email || info.org_contact_phone) && (
            <div
              className="rounded-2xl p-5 mb-6 border"
              style={{ borderColor: `${accent}22`, background: `${accent}08` }}
              data-testid="gift-card-thankyou-org-contact"
            >
              <div className="flex items-center gap-3 mb-3">
                {info.org_logo_url ? (
                  <img src={info.org_logo_url} alt={info.org_name} className="w-9 h-9 rounded-lg object-cover" />
                ) : (
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center text-white font-semibold" style={{ background: accent }}>
                    {info.org_name?.[0] || "•"}
                  </div>
                )}
                <div className="min-w-0">
                  <div className="text-xs uppercase tracking-wide text-gray-500">Any questions?</div>
                  <div className="text-sm font-semibold text-gray-900 truncate">Reach out to {info.org_name}</div>
                </div>
              </div>
              <div className="space-y-2 text-sm">
                {info.org_contact_email && (
                  <a
                    href={`mailto:${info.org_contact_email}`}
                    className="flex items-center gap-2 text-gray-700 hover:text-gray-900 transition-colors"
                    data-testid="gift-card-thankyou-org-email"
                  >
                    <Mail className="w-4 h-4 text-gray-400" />
                    <span className="truncate">{info.org_contact_email}</span>
                  </a>
                )}
                {info.org_contact_phone && (
                  <a
                    href={`tel:${info.org_contact_phone}`}
                    className="flex items-center gap-2 text-gray-700 hover:text-gray-900 transition-colors"
                    data-testid="gift-card-thankyou-org-phone"
                  >
                    <Phone className="w-4 h-4 text-gray-400" />
                    <span>{info.org_contact_phone}</span>
                  </a>
                )}
                {info.org_website && (
                  <a
                    href={info.org_website.startsWith("http") ? info.org_website : `https://${info.org_website}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-gray-700 hover:text-gray-900 transition-colors"
                    data-testid="gift-card-thankyou-org-website"
                  >
                    <Globe className="w-4 h-4 text-gray-400" />
                    <span className="truncate">{info.org_website.replace(/^https?:\/\//, "")}</span>
                  </a>
                )}
              </div>
            </div>
          )}

          {/* Reference + back link */}
          {purchaseId && (
            <p className="text-xs text-gray-400 text-center mb-4">
              Reference:{" "}
              <span className="font-mono">{purchaseId.slice(0, 8)}</span>
            </p>
          )}

          <div className="text-center">
            {loading ? (
              <div className="inline-flex items-center gap-2 text-sm text-gray-400">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading…
              </div>
            ) : (
              <Link
                to="/"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gray-900 hover:bg-gray-700 text-white text-sm font-medium transition-colors"
                data-testid="gift-card-thankyou-home-btn"
              >
                <Home className="w-4 h-4" />
                Back to home
              </Link>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
